<!--
 * @Author: yukizou yukizou@tencent.com
 * @Date: 2025-08-01 21:17:43
 * @LastEditors: yukizou yukizou@tencent.com
 * @LastEditTime: 2025-08-20 20:01:44
-->
<template>
  <div
    v-if="isDrama"
    class="drama-wrap"
    :class="{ 'full-screen': isFullScreen }"
  >
    <div
      class="screen-icon-wrap"
      @click.stop="() => setFullScreenStatus(!isFullScreen)"
    >
      <div
        class="screen-icon"
        :class="[isFullScreen ? 'retract' : 'expand']"
      />
    </div>
    <div
      v-show="isShowDramaReplay"
      class="drama-replay-wrap"
      :style="{ backgroundImage: getImage(activeDrama?.coverImg) }"
    >
      <div class="content-mask">
        <div class="content">
          <div
            class="cover"
            :style="{ backgroundImage: getImage(activeDrama?.coverImg) }"
          />
          <div class="name">
            {{ activeDrama?.name }}
          </div>
          <div class="brief">
            {{ activeDrama?.brief }}
          </div>
          <div class="btns">
            <div
              class="replay"
              @click="replay"
            >
              再看一次
            </div>
            <div
              class="open-app"
              @click="openApp(false, true)"
            >
              观看全剧
            </div>
          </div>
        </div>
      </div>
    </div>
    <slot />
  </div>
  <slot v-else />
</template>

<script lang="ts" setup>
import { computed, toRefs, watch } from 'vue';
import { useStore, MutationTypes } from '@/store/index';
import { fixHttps } from '@/utils/no-import-meta/url';
import useGetCommonMethods from '@/composable/use-get-common-method';
import { useDramaReport } from '@/composable/use-compoent-report-methods';

const dramaReport = useDramaReport();
const emits = defineEmits(['on-replay']);

// props
interface Props {
  isShowDramaReplay: boolean,
}
const props = withDefaults(defineProps<Props>(), {});
const {
  isShowDramaReplay,
} = toRefs(props);

const {
  openSchemaAppendParamsClipboard,
} = useGetCommonMethods();

const store = useStore();
const { state } = store;

const isDrama = computed(() => state.isDrama);
const dramaList = computed(() => state.dramaList || []);
const activeIndex = computed(() => state.activeIndex);
const activeDrama = computed(() => dramaList.value[activeIndex.value]);
const feedsList = computed(() => state.feedsList);
const activeFeed = computed(() => feedsList.value[activeIndex.value]);
const isFullScreen = computed(() => state.isFullScreen);

const getImage = (url: string) => `url(${fixHttps(url)})`;

watch(isShowDramaReplay, (val) => {
  if (val) {
    dramaReport.dramaReplayExpose();
    dramaReport.dramaOpenExpose();
  }
});

const replay = () => {
  emits('on-replay');
  dramaReport.dramaReplayClick();
};

// 切换是否全屏的功能
const setFullScreenStatus = (status: boolean) => {
  store.commit(MutationTypes.SET_IS_FULL_SCREEN, { value: status });

  dramaReport.fullScreenClick(status);
};

const openApp = () => {
  dramaReport.dramaOpenClick();

  const { id: feedId } = activeFeed.value;
  // 打开当前视频，不随精选集变滑动而变化
  openSchemaAppendParamsClipboard(true, `weishi://feed?feed_id=${feedId}&session_from=10000018`);
};
</script>

<style lang="scss">
.drama-wrap {
  position: relative;
  width: 100%;
  flex: 1;
  overflow: hidden;

  &.full-screen {
    height: 100%;
    z-index: 9999;

    .drama-act-wrapper {
      .progress-wrap {
        @include responsiveStyleSingle(margin-bottom, 58, 'px');
      }
    }
  }

  .screen-icon-wrap {
    @include responsiveStyleSingle(top, 95, 'px');
    @include responsiveStyleSingle(right, 15, 'px');
    @include responsiveStyleSingle(padding, 9, 'px');
    position: absolute;
    z-index: 99999;
  }

  .screen-icon {
    @include responsiveStyleSingle(width, 40, 'px');
    @include responsiveStyleSingle(height, 40, 'px');

    &.expand {
      background: url('https://isee.weishi.qq.com/share/video-expand.png') no-repeat;
      background-size: 100% auto;
    }
    &.retract {
      background: url('https://isee.weishi.qq.com/share/video-retract.png') no-repeat;
      background-size: 100% auto;
    }
  }

  .drama-replay-wrap {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 20;
    background-size: cover;
    background-position: center center;

    .content-mask {
      fill: color(display-p3 0 0 0 / 0.40);
      backdrop-filter: blur(8px);
      background-color: rgba(0, 0, 0, 0.4);
      position: absolute;
      top: 0px;
      bottom: 0;
      left: 0;
      right: 0;
    }

    .content {
      color: #fff;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      @include responsiveStyleSingle(top, 80, 'px');

      .cover {
        @include responsiveStyleSingle(width, 209, 'px');
        @include responsiveStyleSingle(height, 291, 'px');
        @include responsiveStyleSingle(border-radius, 15, 'px');
        background-size: cover;
        background-position: center center;
      }

      .name {
        @include responsiveStyleSingle(margin-top, 23, 'px');
        @include responsiveStyleSingle(margin-bottom, 16, 'px');
        @include responsiveStyleSingle(font-size, 32, 'px');
        font-weight: 600;
      }

      .brief {
        @include responsiveStyleSingle(width, 560, 'px');
        @include responsiveStyleSingle(font-size, 28, 'px');
        @include responsiveStyleSingle(margin-bottom, 48, 'px');
        color: #C6C6C6;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .btns {
        display: flex;
        position: relative;
        align-items: center;
        justify-content: space-between;
        margin-left: auto;
        margin-right: auto;

        & > div {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 1;
          @include responsiveStyleSingle(width, 244,'px');
          @include responsiveStyleSingle(height, 72,'px');
          @include responsiveStyleSingle(font-size, 32, 'px');
          @include responsiveStyleSingle(border-radius, 16, 'px');
        }

        .replay {
          @include responsiveStyleSingle(margin-right, 24, 'px');
          background: var(---, color(display-p3 1 1 1 / 0.20));
        }
        .open-app {
          background: var(---, color(display-p3 0.4627 0.2588 0.9608));
        }
      }
    }
  }

  .video-wrapper {
    .play-status {
      @include responsiveStyleSingle(top, 80, 'px');
      @include responsiveStyleSingle(bottom, 0, 'px');
    }
  }
}
</style>
